import React from 'react';
import { getData, getId } from '../../../utils/item.js';
import { BoardGridMap } from './board.GridMap.js';
import { BoardResizeHandle } from '../boardResizeHandle.js';

const mergeMapWidgetElement = element => {
  const elementId = getId(element);
  const payload = getData(element) || {};
  return {
    [elementId]: {
      ...payload,
      ...(payload?.map || {}),
    },
  };
};

const isInteractiveWidgetTarget = target => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return Boolean(
    target.closest(
      'button, input, textarea, select, option, label, a, [role="button"], [contenteditable="true"], [data-map-widget-resize-handle="true"], .board-grid-no-drag, [data-board-widget-interactive="true"]',
    ),
  );
};

const isEventFromModal = event =>
  event?.target instanceof Element &&
  Boolean(event.target.closest('[data-modal-root="true"]'));

export const BoardMapWidgetsLayer = ({
  widgets = [],
  tab,
  editable = false,
  widgetEditable = true,
  selectedWidgetId = null,
  selectedWidgetIds = [],
  layoutOverrides = {},
  onSelectWidget,
  onWidgetDragStart,
  onWidgetResizeStart,
  didWidgetDragRef,
  offsetX = 0,
  offsetY = 0,
  getWidgetNodeId,
}) => (
  <>
    {widgets.map(widget => {
      const elementId = getId(widget);
      const payload = getData(widget) || {};
      const layout = layoutOverrides[elementId] || payload?.map || {};
      const x = Number(layout?.x) || 0;
      const y = Number(layout?.y) || 0;
      const w = Math.max(80, Number(layout?.w) || 0);
      const h = Math.max(24, Number(layout?.h) || 0);
      const isSelected =
        selectedWidgetId === elementId || selectedWidgetIds.includes(elementId);

      return (
        <div
          key={elementId}
          className={`absolute group z-20 ${editable ? 'cursor-move' : ''}`}
          data-map-widget-root='true'
          data-map-node-id={getWidgetNodeId?.(elementId) || undefined}
          style={{
            left: x + offsetX,
            top: y + offsetY,
            width: w,
            height: h,
            zIndex: 18,
          }}
          onPointerDownCapture={event => {
            if (isEventFromModal(event)) {
              return;
            }
            onSelectWidget?.(elementId);
            if (!editable) {
              return;
            }
            if (isInteractiveWidgetTarget(event.target)) {
              return;
            }
            onWidgetDragStart?.(event, elementId);
          }}
          onClickCapture={event => {
            if (isEventFromModal(event)) {
              return;
            }
            const draggedWidgetIds = Array.isArray(didWidgetDragRef?.current)
              ? didWidgetDragRef.current
              : [];
            if (draggedWidgetIds.includes(elementId)) {
              didWidgetDragRef.current = draggedWidgetIds.filter(
                widgetId => widgetId !== elementId,
              );
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onSelectWidget?.(elementId);
          }}
        >
          <BoardGridMap
            tab={tab}
            type='map'
            element={mergeMapWidgetElement(widget)}
            editable={widgetEditable}
            widgetEditable={widgetEditable}
            getTextHeight={() => null}
            gridLayoutKey={`${elementId}-map`}
            boardWidth={w}
            rowHeight={30}
            cols={12}
          />
          <div className='absolute inset-0 pointer-events-none z-20'>
            {isSelected && (
              <div className='absolute inset-0 rounded border-2 border-secondary' />
            )}
            <BoardResizeHandle
              dataAttrName='data-map-widget-resize-handle'
              visible={editable && isSelected}
              onPointerDown={event => {
                event.stopPropagation();
                onWidgetResizeStart?.(event, elementId);
              }}
            />
          </div>
        </div>
      );
    })}
  </>
);
